import { DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, filter, map, merge, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';
import { EnrolledMSMEList } from '../../../../../shared/models/EnrolledMSMEList';
import { ApiService } from '../../../../../shared/services/api.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { StorageService } from '../../../../../shared/services/storage.service';
import { API_ENDPOINTS } from '../../../../../shared/utils/const';

@Component({
  selector: 'app-viewattendedmsme',
  templateUrl: './viewattendedmsme.component.html',
  styleUrl: './viewattendedmsme.component.css'
})
export class ViewattendedmsmeComponent {

  private loadDataSubscription: Subscription = new Subscription();
  private destroy$: Subject<void> = new Subject<void>();
  eventId: any;

  dataSource!: MatTableDataSource<EnrolledMSMEList>
  displayedColumns: string[] = ['Sr.No', 'UdyamRegistrationNumber', 'OwnersMobileNumber', 'IsRAMPRegistered','RegistrationType', 'Action']
  @ViewChild(MatPaginator) pagination!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  term$ = new BehaviorSubject<string>('');
  codeSearch$ = new BehaviorSubject<any>(null);
  nameSearch$ = new BehaviorSubject<any>(null);
  searchText: string = '';
  codeSearchKey: string = '';
  nameSearchKey: string = '';

  length: number = 0;
  pageSize: number = 20;
  displayedData: any[] = [];
  reportData: any[] = [];
  allrecord: any[] = [];
  pageSizeOptions: number[] = [10, 20, 50, 100];
  hidePageSize = false;
  showPageSizeOptions = true;
  showFirstLastButtons = true;
  disabled = false;
  pageIndex = 0;
  pageEvent!: PageEvent;

  DtoPagination =
    {
      "UniqueEventId": "",
      "PageNumber": 1,
      "PageSize": 20,
      "SortColumn": "",
      "SortDirection": "",
      "SearchTerm": "",
      "SearchCol": ""
    }

  totalGrievances = 0;
  disposedGrievances = 0;
  pendingGrievances = 0;
  rejectedGrievances = 0;
  participantsAttended = 0;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private toastrService: ToastrService,
    private storageService: StorageService,
    public commonService: CommonService,
    private datePipe: DatePipe,
    private route: ActivatedRoute) { }

  async ngOnInit(): Promise<void> {
    this.route.params.subscribe(params => {
      this.eventId = params['eventid'];
    });
    this.DtoPagination.PageSize = 20;
    this.DtoPagination.SearchCol = this.commonService.searchText;
  }

  ngAfterViewInit() {
    this.loadData();
  }

  loadData() {
    this.DtoPagination.UniqueEventId = this.eventId;
    if (!this.pagination) return;

    // Unsubscribe from previous subscription to prevent multiple API calls
    this.loadDataSubscription.unsubscribe();

    // Reset subscription with a new request
    this.loadDataSubscription = merge(
      this.sort.sortChange.pipe(takeUntil(this.destroy$)),
      this.term$.pipe(debounceTime(1100), distinctUntilChanged(), takeUntil(this.destroy$)),
      this.pagination.page.pipe(takeUntil(this.destroy$))
    )
      .pipe(
        filter(searchTerm => searchTerm != null),
        switchMap(() => {
          this.pagination.pageIndex = 0;
          this.DtoPagination.SortColumn = this.sort.active;
          this.DtoPagination.SortDirection = this.sort.direction;
          this.DtoPagination.SearchCol = this.commonService.searchText;

          return this.apiService.getRequest(API_ENDPOINTS.IA.GETATTENDEDMSMELISTFORIA, this.DtoPagination)
            .pipe(catchError(() => of(null)));
        }),
        map(resp => {
          if (resp && resp.status) {
            this.length = resp['totalRecords'];
          }
          return resp?.data || {};
        })
      )
      .subscribe(data => {
        this.dataSource = data;
        this.adjustPaginator();
      });
  }

  ngOnDestroy() {
    this.loadDataSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  adjustPaginator() {
    const totalPages = Math.ceil(this.length / this.pageSize);

    // If the current page index is greater than or equal to total pages, move to the last page
    if (this.pageIndex >= totalPages) {
      this.pageIndex = totalPages - 1; // Set to the last valid page
      this.DtoPagination.PageNumber = this.pageIndex + 1; // Update for 1-based pagination
    }

    // Update the paginator with the adjusted page index
    this.pagination.pageIndex = this.pageIndex;
  }

  onPageChange(e: PageEvent) {
    this.pageEvent = e;
    this.length = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;

    this.DtoPagination.PageNumber = e.pageIndex + 1;
    this.DtoPagination.PageSize = e.pageSize;

    this.loadData();
  }

  form = new FormGroup({
    searchControl: new FormControl('', [
      Validators.pattern(this.commonService.searchFilter)
    ])
  })

  onSearchClick(searchValue: string): void {
    const searchControl = this.form.get('searchControl');
    searchControl?.markAsTouched();

    if (searchControl?.valid && searchValue.trim() !== '') {
      this.DtoPagination.SearchTerm = searchValue.trim();
      this.DtoPagination.SearchCol = this.commonService.searchText;
      this.loadData();
    } else {
      this.resetPage();
    }
  }

  onVerifyMSME(udyamRegistrationNumber: string, ownersMobileNumber: string): void {

    const formdatavalue = {
      UdyamNumber: udyamRegistrationNumber,
      MobileNumber: ownersMobileNumber,
      UniqueEventId: this.eventId
    };
    this.router.navigate(['/verifyOnSpotMSME'], { state: { formData: formdatavalue } });
  }

  exportToExcel(): void {
    const fileName = 'AttendedMSME.xlsx';

    const DtoPagination = {
      UniqueEventId: this.eventId,
      PageNumber: 1,
      PageSize: 9999999,
      SortColumn: this.sort.active,
      SortDirection: this.sort.direction,
      SearchTerm: "",
      SearchCol: this.commonService.searchText,
    };

    this.apiService.getRequest(API_ENDPOINTS.IA.GETATTENDEDMSMELISTFORIA, DtoPagination).subscribe(
      result => {
        if (result.status) {
          this.allrecord = result.data;

          if (!this.allrecord || this.allrecord.length === 0) {
            this.toastrService.error('No data available to export.');
            return;
          }

          const excelData = this.allrecord.map((item, index) => ({
            'Sr.No': item.rowNum,
            'Udyam Registration Number': item.udyamRegistrationNumber,
            'Owners Mobile Number': item.ownersMobileNumber,
            'Is User Registered on RAMP': item.isRAMPRegistered ? 'Yes': 'No',
          }));

          const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);

          const columnWidths = [
            { wch: 10 },
            { wch: 15 },
            { wch: 30 },
            { wch: 50 },
            { wch: 20 },
            { wch: 15 },
            { wch: 20 },
          ];
          worksheet['!cols'] = columnWidths;

          const workbook: XLSX.WorkBook = {
            Sheets: { 'Grievance List': worksheet },
            SheetNames: ['Grievance List'],
          };

          const excelBuffer: any = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array',
          });

          const data: Blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
          });

          saveAs(data, fileName);
          this.toastrService.success('Excel exported successfully!');
        }
      }
    );
  }

  saveExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    saveAs(data, fileName);
  }

  resetPage() {
    this.DtoPagination.SearchTerm = "";
    this.DtoPagination.SearchCol = this.commonService.searchText;
    this.loadData();
  }

  onPressBack() {
    this.router.navigate(['/view_event']);
  }
}
